/**
 * Theme tokens and base theme configuration.
 * This module defines all design tokens (colors, typography, spacing) used across the application.
 * It is imported by ThemeProvider to create the runtime theme.
 */

import { componentOverrides } from './componentOverrides';
import { typography } from './typographyTokens';

export { spacingTokens } from './spacingTokens';
export { typographyTokens } from './typographyTokens';
export { typography } from './typographyTokens';

export const newUiColorTokens = {
    prime: {
        blue: '#2372B9',
        orange: '#F47B27',
        green: '#6BA543',
        granite: '#7E8083',
    },
    primeAdditional: {
        midBlue: '#007FA4',
        lightBlue: '#E9F1F8',
        lightOrange: '#FBB254',
        midGreen: '#C4DBB4',
        lightGreen: '#F0F6EC',
        lightGreenContainerFill: '#E1FDD9',
    },
    supporting: {
        teal: '#007FA4',
        cantaloupe: '#FBB254'
    },
    supportingAdditional: {
        tealContainerStroke: '#B3D9E4',
        tealContainerFill: '#E6F2F6',
        tealUltraLight: '#F5FDFF',
        cantaloupeContainerStroke: '#FDE0BB',
        cantaloupeContainerFill: '#FEF0DD',
        cantaloupeUltraLight: '#FFF7EE',
    },
    content: {
        default: '#12395D',
        white: '#FFFFFF',
        black: '#000000',
    },
    surface: {
        level1: '#F5F5F5',
        level2: '#FBFBFB',
    },
    elements: {
        divider: '#E5E6E6',
        alert: '#E2624A',
    },
    feedback: {
        approval: '#6BA543',
        changeRequested: '#FBB254',
        awaitingReview: '#007FA4',
    },
    backgroundColors: {
        default: '#F5F5F5',
        paper: '#FFFFFF',
    },
} as const;

export const legacyColorTokens = {
    primary: {
        main: '#2372B9',
    },
    gray: {
        main: '#222222',
        solidGray: '#7e8083',
        lighter: '#7e808340',
        borderGray: '#424242',
        darkGray1: '#646669',
        lightGray: '#F2F2F2',
        lightGray1: '#E5E9EA',
        lightGray2: '#F8F9FA',
        lightGray3: '#FBFCFC',
        lightGray4: '#F5F5F5',
    },
    solidBlue: {
        main: '#2372B9',
        lightHover: 'rgb(35,114,185, .05)',
        lightActive: '#EDF4F9',
    },
    darkGreen: {
        main: '#6BA543',
        lightGreen: '#CADB2E',
    },
    blue: {
        main: '#007fa4',
        blue1: '#1F67A6',
        blue2: '#1D5F99',
        focus: '#BCD4EA',
        lightBlue1: '#F3F8FC',
        lightBlue2: '#EDF4F9',
    },
    orange: {
        main: '#f47b27',
        lightOrange: '#fbb254',
    },
    yellow: {
        main: '#FFEF00',
        darkYellow: '#FFD700',
    },
    deepRed: {
        main: '#B70000',
        solidRed: '#FF3333',
    },
    white: {
        main: '#FFFFFF',
    },
    black: {
        main: '#000000',
        light: '#333333',
        dark: '#000000',
        contrastText: '#FFFFFF',
    },
} as const;

const status = {
    status1: '#EDF4FA',
    status2: '#FFFBF6',
    status3: '#F3F8F0',
} as const;

export const baseTheme = {
    // Legacy status tokens for existing screens. These are used in the legacy design system and should be migrated to new tokens.
    ...status,
    palette: {
        // Standard MUI semantic tokens for all new and refactored screens.
        // contrastThreshold: 4.5,
        // tonalOffset: {
        //     light: 0.1,
        //     dark: 0.2,
        // },
        // common: {
        //     black: '#000000',
        //     white: '#FFFFFF',
        // },

        // Custom color tokens for new UI screens. These are used in the new design system and should be preferred over legacy tokens.
        ...newUiColorTokens,

        // Legacy custom tokens kept intact for existing screens until migration.
        ...legacyColorTokens,
    },

    typography: typography,
    spacing: 8,
    components: componentOverrides,
};
